import type { File } from "@/services/fobizzToolsApi";
import {
  GradeLevelEnum,
  SubjectTypeEnum,
  SchoolTypeEnum,
  WorksheetLanguageEnum,
  LanguageLevelEnum,
  AppEnum,
  AppNamespaceEnum,
  ConversationStyleEnum,
  LanguageEnum,
  StatusEnum,
} from "@/services/fobizzToolsApi";

export interface UserInput {
  pseudonym?: string;
  llModel?: string;
  systemPrompt?: string;
  conversationStyle?: ConversationStyleEnum;
}

export interface Subscriber {
  id: string;
  email: string;
  firstname: string;
  lastname: string;
  displayName: string;
  publicDisplayName: string;
  pseudonym: string;
  imageUrl: string;
  initials: string;
  visitor: boolean;
}

export type StockImageProvider = "pixabay" | "pexels";
export type StockImageType = "photo" | "illustration" | "vector";

export interface StockImage {
  results: StockImageResult[];
  total: number;
}

export interface StockImageResult {
  id: string;
  filename: string;
  width: number;
  height: number;
  urls: StockImageResultUrls;
  user: StockImageResultUser;
  pageUrl: string;
}

export interface StockImageResultUrls {
  regular: string;
  thumb: string;
}

export interface StockImageResultUser {
  id: string;
  username: string;
  url: string;
}

export interface LinkPreview {
  title: string;
  description: string;
  url: string;
  error: string;
  images: string[];
  videos: string[];
}

export interface RailsBlob {
  id: string;
  key: string;
  filename: string;
  content_type: string;
  byte_size: number;
  signed_id: string;
}

export interface RouteRecord {
  name?: string;
  path: string;
  component?: Component;
  redirect?: string;
}

export type ActionCableCallback = (
  action: string,
  userId: string,
  payload: string
) => void;

export interface ActionCableData {
  payload: string;
}

export interface ActionCableUserActionData extends ActionCableData {
  action: string;
  user_id: string;
}

export interface ActionCableJobStatusData extends ActionCableUserActionData {
  status: StatusEnum;
  message?: string;
  exception?: string;
  progress?: number;
  stage?: string;
  name?: string;
  path?: string;
}

export type ActionCableChannelPayload = {
  [key: string]: string | number | boolean;
};
export type ActionCableSubscription = { unsubscribe: () => void };

export type DraggableEvent = {
  newIndex: number;
  item: {
    dataset: {
      id: string;
    };
  };
  added: {
    newIndex: number;
    element: HTMLElement;
  };
  moved: {
    newIndex: number;
    element: HTMLElement;
  };
};

export const subjectTypeKeys = Object.values(SubjectTypeEnum).filter(
  Number.isInteger
) as number[];
export const schoolTypeKeys = Object.values(SchoolTypeEnum).filter(
  Number.isInteger
) as number[];
export const gradeLevelKeys = Object.values(GradeLevelEnum).filter(
  Number.isInteger
) as number[];
export const languageLevelKeys = Object.values(LanguageLevelEnum) as string[];
export const worksheetLanguageKeys = Object.values(
  WorksheetLanguageEnum
) as string[];

export type PaginatedItems<T> = {
  collection: Array<T>;
  totalPages: number;
  totalCount: number;
  currentPage: number;
  nextPage: number | null;
  prevPage: number | null;
  offsetValue: number;
  previousPagePath: string | null;
  nextPagePath: string | null;
};

export interface TItemQueryKeys {
  all: () => readonly string[];
  lists: () => readonly string[];
  list: (page: Ref<number>) => readonly (string | Ref<number>)[];
  detail: (id: string) => readonly string[];
}

export interface TItemRoutes {
  index: string;
  show: string;
  share: string;
  settings: string;
}

export const LANGUAGES = ["de", "en", "fr", "es"] as const;
export type LanguageType = (typeof LANGUAGES)[number];

export const APP_NAMESPACES = [
  "ai_image",
  "ai_speech_recognition",
  "worksheet",
  "classroom_screen",
  "pinboard",
  "surveys",
  "text_editor",
  "website",
  "video_recorder",
  "audio_recorder",
  "screen_recorder",
  "word_clouds",
  "link_shortener",
  "file_sharing",
  "qrcodes",
];
export type AppNamespace = (typeof APP_NAMESPACES)[number];

export interface Limits {
  maxItemsCount: number;
  maxRecordingLength?: number;
}

export enum AccordionItems {
  Name = "name",
  RecommendedFor = "recommended_for",
  PrivacyLevel = "privacy-level",
  AutoCleanup = "auto-cleanup",
  EmbedIframe = "embed-iframe",
  SchoolClasses = "school-classes",
}

export interface RailsInvalidRecordError {
  base: string[];
  [key: string]: string[];
}

export interface AiCategory {
  filterType: string;
  value: string;
  icon: string;
  color: string;
}

export interface FederatedSearch {
  myResults: FederatedSearchResult[];
  galleryResults: FederatedSearchResult[];
}

export interface FederatedSearchResult {
  id: string;
  name: string;
  url: string;
  clientPath: string;
  shareUrl: string;
  icon: string;
  color: string;
}

export enum ChartType {
  BAR = "bar",
  PIE = "pie",
}

export interface UserPdf {
  id: string;
  title: string;
  url: string;
  file?: File;
  createdAt: string;
  updatedAt: string;
}

export type TDeleteActionJobResponse = {
  kind: AppEnum;
  namespace: AppNamespaceEnum;
  id: string;
  tabId: string;
};

export type TDuplicateActionJobResponse = {
  kind: AppEnum;
  namespace: AppNamespaceEnum;
  id: string;
  name: string;
  duplicateParentId: string;
  tabId: string;
};

export type TClearDiscardedActionJobResponse = {
  tabId: string;
};

export type TUppyUploadError = {
  name: string;
  message: string;
  details?: string;
};

export const languageKeys = Object.values(LanguageEnum) as string[];

export const VoiceEnum = {
  openai_alloy: "openai_alloy",
  openai_echo: "openai_echo",
  openai_fable: "openai_fable",
  openai_onyx: "openai_onyx",
  openai_nova: "openai_nova",
  openai_shimmer: "openai_shimmer",
  elevenlabs_otto: "elevenlabs_otto",
  elevenlabs_petra: "elevenlabs_petra",
} as const;

export type VoiceEnum = (typeof VoiceEnum)[keyof typeof VoiceEnum];
export const voices = Object.values(VoiceEnum) as string[];
